import gsap from "gsap";
import { SplitText } from "gsap/SplitText";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { wrapInMask, unwrapMask } from "../../utils/wrapInMask";
import { createScrollReveal } from "../../utils/createScrollReveal";
import { lerpfi } from "../../utils/lerp";
import { observeMediaElement } from "../../utils/mediaTelemetry";

gsap.registerPlugin(SplitText, ScrollTrigger);

const EASE = "expo.out";

// When true, the cloud is rendered directly as an alpha-channel <video>.
// When false, the legacy WebGL compositor reads a side-by-side RGB+alpha
// texture from cloud_comp.mp4 and renders it into the <canvas>.
const USE_VIDEO_ALPHA = true;

const driftConfig = {
  xFreq1: 0.45, xAmp1: 0.85,
  xFreq2: 0.49, xAmp2: 0.7000000000000001,
  yFreq1: 0.2, yAmp1: 0.4,
  yFreq2: 0.68, yAmp2: 0.9,
  rFreq1: 0.29000000000000004, rAmp1: 0.24,
  rFreq2: 0.65, rAmp2: 0.43,
};

// Mouse parallax (desktop only): max offset in % of the cloud size, and the
// lerpfi smoothing (frame-rate independent).
const MOUSE_AMP_X = 3;
const MOUSE_AMP_Y = 4;
// Rotation is driven by horizontal mouse velocity (px/ms * KICK = degrees),
// capped at MAX, then it decays back to 0.
const MOUSE_ROT_KICK = 0.5;
const MOUSE_ROT_MAX = 8;
const MOUSE_ROT_DECAY = 0.056;
const MOUSE_ROT_SMOOTHING = 0.05;
const MOUSE_SMOOTHING = 0.05;

export default class CreatorsPush {
  constructor(el) {
    this.el = el;
    this.cloudImg = el.querySelector(".c-creators-push__cloud-img");
    this.cloudVideo = el.querySelector(".c-creators-push__cloud-src");
    this.driftEl = USE_VIDEO_ALPHA ? this.cloudVideo : this.cloudImg;
    if (this.cloudVideo) {
      this.stopTracking = observeMediaElement(this.cloudVideo, {
        context: "creators-push",
        kind: "cloud",
        src: this.cloudVideo.dataset.src || this.cloudVideo.currentSrc,
      });
    }

    this.rafId = null;
    this.isVisible = false;
    this.startTime = 0;

    this.gl = null;
    this.glReady = false;

    this.videoLoadPromise = null;
    this.useVFC = typeof HTMLVideoElement !== "undefined"
      && typeof HTMLVideoElement.prototype.requestVideoFrameCallback === "function";
    this.vfcHandle = null;
    this.lastDrawAt = 0;

    // Random phase offsets so each page load looks slightly different.
    this.phases = {
      x1: Math.random() * Math.PI * 2,
      x2: Math.random() * Math.PI * 2,
      y1: Math.random() * Math.PI * 2,
      y2: Math.random() * Math.PI * 2,
      r1: Math.random() * Math.PI * 2,
      r2: Math.random() * Math.PI * 2,
    };

    this.canMouse = window.matchMedia("(hover: hover) and (pointer: fine)").matches
      && !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.mouse = { tx: 0, ty: 0, x: 0, y: 0, lastX: null, lastT: 0, rot: 0, rotTarget: 0 };
    this.onPointerMove = this.onPointerMove.bind(this);

    this.tick = this.tick.bind(this);
    this.onVideoFrame = this.onVideoFrame.bind(this);

    this.setupReveal();
    this.setupCloudDrift();
  }

  ensureVideoLoaded() {
    if (this.videoLoadPromise) return this.videoLoadPromise;
    if (!this.cloudImg || !this.cloudVideo) {
      this.videoLoadPromise = Promise.resolve();
      return this.videoLoadPromise;
    }

    this.videoLoadPromise = new Promise((resolve) => {
      if (USE_VIDEO_ALPHA) {
        const onCanPlay = () => {
          this.cloudVideo.removeEventListener("loadedmetadata", onCanPlay);
          resolve();
        };
        if (this.cloudVideo.readyState >= 1) onCanPlay();
        else this.cloudVideo.addEventListener("loadedmetadata", onCanPlay, { once: true });
        this.cloudVideo.load();
        return;
      }

      const paintMiddleFrame = () => {
        const dur = this.cloudVideo.duration;
        if (!isFinite(dur) || dur <= 0) {
          resolve();
          return;
        }
        let settled = false;
        const onSeeked = () => {
          if (settled) return;
          settled = true;
          clearTimeout(fallback);
          this.cloudVideo.removeEventListener("seeked", onSeeked);
          this.drawCloudFrame();
          resolve();
        };
        // Fallback: if the seek never completes (slow network, bad mux), give
        // up after 3s and proceed without a placeholder paint — playback will
        // start later via the canplay path and rVFC takes over from there.
        const fallback = setTimeout(onSeeked, 3000);
        this.cloudVideo.addEventListener("seeked", onSeeked);
        this.cloudVideo.currentTime = dur * 0.5;
      };

      const onMetadata = () => {
        const vw = this.cloudVideo.videoWidth;
        const vh = this.cloudVideo.videoHeight;
        if (!vw || !vh) {
          resolve();
          return;
        }
        this.cloudImg.width = vw / 2;
        this.cloudImg.height = vh;
        this.initWebGL();
        paintMiddleFrame();
      };

      if (this.cloudVideo.readyState >= 1) {
        onMetadata();
      } else {
        this.cloudVideo.addEventListener("loadedmetadata", onMetadata, { once: true });
      }

      const src = this.cloudVideo.dataset.src;
      if (src && !this.cloudVideo.src) {
        this.cloudVideo.src = src;
        this.cloudVideo.load();
      }
    });

    return this.videoLoadPromise;
  }

  waitForPlayable() {
    if (this.canPlayPromise) return this.canPlayPromise;
    if (!this.cloudVideo) {
      this.canPlayPromise = Promise.resolve();
      return this.canPlayPromise;
    }

    this.canPlayPromise = new Promise((resolve) => {
      if (this.cloudVideo.readyState >= 4) {
        resolve();
        return;
      }
      let settled = false;
      const done = () => {
        if (settled) return;
        settled = true;
        clearTimeout(fallback);
        this.cloudVideo.removeEventListener("canplaythrough", done);
        this.cloudVideo.removeEventListener("canplay", done);
        resolve();
      };
      this.cloudVideo.addEventListener("canplaythrough", done);
      // If canplaythrough never fires within 5s, accept canplay as good enough.
      const fallback = setTimeout(() => {
        this.cloudVideo.addEventListener("canplay", done, { once: true });
        // And if even canplay never fires, force-resolve after another 5s.
        setTimeout(done, 5000);
      }, 5000);
    });

    return this.canPlayPromise;
  }

  tryPlay() {
    if (!this.cloudVideo) return;
    const p = this.cloudVideo.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
  }

  initWebGL() {
    const gl = this.cloudImg.getContext("webgl", { premultipliedAlpha: true, alpha: true, antialias: false });
    if (!gl) return;

    const vsSrc = `
      attribute vec2 a_pos;
      varying vec2 v_uv;
      void main() {
        v_uv = vec2((a_pos.x + 1.0) * 0.5, 1.0 - (a_pos.y + 1.0) * 0.5);
        gl_Position = vec4(a_pos, 0.0, 1.0);
      }
    `;
    const fsSrc = `
      precision mediump float;
      uniform sampler2D u_tex;
      varying vec2 v_uv;
      void main() {
        vec3 rgb = texture2D(u_tex, vec2(v_uv.x * 0.5, v_uv.y)).rgb;
        float a  = texture2D(u_tex, vec2(v_uv.x * 0.5 + 0.5, v_uv.y)).r;
        gl_FragColor = vec4(rgb * a, a);
      }
    `;

    const compile = (type, src) => {
      const s = gl.createShader(type);
      gl.shaderSource(s, src);
      gl.compileShader(s);
      return s;
    };
    const prog = gl.createProgram();
    gl.attachShader(prog, compile(gl.VERTEX_SHADER, vsSrc));
    gl.attachShader(prog, compile(gl.FRAGMENT_SHADER, fsSrc));
    gl.linkProgram(prog);
    gl.useProgram(prog);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, -1,1, 1,-1, 1,1]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, "a_pos");
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    const tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);

    gl.viewport(0, 0, this.cloudImg.width, this.cloudImg.height);
    gl.clearColor(0, 0, 0, 0);

    this.gl = gl;
    this.glTex = tex;
    this.glReady = true;
  }

  drawCloudFrame() {
    if (!this.glReady) return;
    if (this.cloudVideo.readyState < 2) return;
    const gl = this.gl;
    gl.bindTexture(gl.TEXTURE_2D, this.glTex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this.cloudVideo);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }

  setupReveal() {
    this.reveal = createScrollReveal({
      trigger: this.el,
      build: () => {
        const cloud = this.el.querySelector(".c-creators-push__cloud");
        const title = this.el.querySelector(".c-creators-push__title");
        const text = this.el.querySelector(".c-creators-push__text");
        const detailsDesktop = [...this.el.querySelectorAll(".c-creators-push__details--desktop > p")];
        const detailsMobile = [...this.el.querySelectorAll(".c-creators-push__details--mobile p")];

        if (cloud) cloud.style.opacity = "0";
        if (text) text.style.opacity = "0";

        const splits = [];
        const masks = [];
        const splitLines = (el) => {
          const split = new SplitText(el, { type: "lines" });
          splits.push(split);
          split.lines.forEach((line) => masks.push(wrapInMask(line)));
          return split.lines;
        };

        const titleLines = title ? splitLines(title) : [];
        const detailsDesktopLines = detailsDesktop.flatMap(splitLines);
        const detailsMobileLines = detailsMobile.flatMap(splitLines);

        const tl = gsap.timeline({ delay: 0.2, paused: true });

        if (cloud) {
          tl.fromTo(cloud, { opacity: 0 }, { opacity: 1, duration: 1.2, ease: EASE }, 0);
        }
        if (titleLines.length) {
          tl.fromTo(titleLines, { y: "120%" }, { y: "0%", duration: 1.2, ease: EASE, stagger: 0.2 }, 0.2);
        }
        if (detailsDesktopLines.length) {
          tl.fromTo(detailsDesktopLines, { y: "110%" }, { y: "0%", duration: 1.2, ease: EASE }, "-=0.8");
        }
        if (text) {
          tl.fromTo(text, { opacity: 0 }, { opacity: 1, duration: 1.2, ease: EASE }, "-=0.8");
        }
        if (detailsMobileLines.length) {
          tl.fromTo(detailsMobileLines, { y: "110%" }, { y: "0%", duration: 1.2, ease: EASE }, "-=0.8");
        }

        return {
          tl,
          teardown: () => {
            splits.forEach((s) => s.revert());
            masks.forEach(unwrapMask);
          },
        };
      },
    });
  }

  setupCloudDrift() {
    if (!this.driftEl) return;

    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.isVisible = entry.isIntersecting;
        if (this.isVisible) {
          this.ensureVideoLoaded().then(() => {
            if (!this.isVisible) return;
            // Middle frame is painted and the drift can begin animating it
            // even before playback starts buffering enough frames.
            this.start();
            this.waitForPlayable().then(() => {
              if (!this.isVisible) return;
              this.tryPlay();
            });
          });
        } else {
          this.stop();
        }
      },
      { rootMargin: "50% 0px" }
    );
    this.observer.observe(this.el);
  }

  onPointerMove(e) {
    const m = this.mouse;
    if (m.lastX != null) {
      const vx = (e.clientX - m.lastX) / Math.max(e.timeStamp - m.lastT, 8);
      const target = Math.max(-MOUSE_ROT_MAX, Math.min(MOUSE_ROT_MAX, vx * MOUSE_ROT_KICK));
      // Only ever push the angle further; the decay in tick() brings it back.
      if (Math.abs(target) > Math.abs(m.rotTarget)) m.rotTarget = target;
    }
    m.lastX = e.clientX;
    m.lastT = e.timeStamp;
    this.mouse.tx = (e.clientX / window.innerWidth) * 2 - 1;
    this.mouse.ty = (e.clientY / window.innerHeight) * 2 - 1;
  }

  start() {
    if (this.canMouse && !this.mouseBound) {
      window.addEventListener("pointermove", this.onPointerMove, { passive: true });
      this.mouseBound = true;
    }
    if (this.rafId == null) {
      this.startTime = performance.now();
      this.lastTickAt = 0;
      this.rafId = requestAnimationFrame(this.tick);
    }
    if (!USE_VIDEO_ALPHA && this.useVFC && this.vfcHandle == null && this.cloudVideo) {
      this.vfcHandle = this.cloudVideo.requestVideoFrameCallback(this.onVideoFrame);
    }
  }

  stop() {
    if (this.mouseBound) {
      window.removeEventListener("pointermove", this.onPointerMove);
      this.mouseBound = false;
      this.mouse.lastX = null;
    }
    if (this.cloudVideo && !this.cloudVideo.paused) this.cloudVideo.pause();
    if (this.rafId != null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    // No cancel API for rVFC — just stop re-scheduling. Drop the handle so a
    // late callback that fires once after stop() doesn't redraw.
    this.vfcHandle = null;
  }

  onVideoFrame() {
    if (!this.isVisible) {
      this.vfcHandle = null;
      return;
    }
    this.drawCloudFrame();
    if (this.cloudVideo) {
      this.vfcHandle = this.cloudVideo.requestVideoFrameCallback(this.onVideoFrame);
    }
  }

  tick(now) {
    // Time in seconds, slowed down for a gentle drift.
    const t = (now - this.startTime) / 1000;

    // Sum of two sines at incommensurate frequencies → smooth, non-repeating motion.
    const c = driftConfig;
    const x =
      Math.sin(t * c.xFreq1 + this.phases.x1) * c.xAmp1 +
      Math.sin(t * c.xFreq2 + this.phases.x2) * c.xAmp2;
    const y =
      Math.sin(t * c.yFreq1 + this.phases.y1) * c.yAmp1 +
      Math.sin(t * c.yFreq2 + this.phases.y2) * c.yAmp2;
    const r =
      Math.sin(t * c.rFreq1 + this.phases.r1) * c.rAmp1 +
      Math.sin(t * c.rFreq2 + this.phases.r2) * c.rAmp2;

    // Ease the mouse offset toward the pointer position.
    const m = this.mouse;
    const dt = Math.min(now - (this.lastTickAt || now), 50);
    this.lastTickAt = now;
    m.x = lerpfi(m.x, m.tx, MOUSE_SMOOTHING, dt);
    m.y = lerpfi(m.y, m.ty, MOUSE_SMOOTHING, dt);
    m.rotTarget = lerpfi(m.rotTarget, 0, MOUSE_ROT_DECAY, dt);
    m.rot = lerpfi(m.rot, m.rotTarget, MOUSE_ROT_SMOOTHING, dt);

    // x/y in %, rotation in deg.
    const fx = x + m.x * MOUSE_AMP_X;
    const fy = y + m.y * MOUSE_AMP_Y;
    const fr = r + m.rot;
    this.driftEl.style.transform = `translate(${fx.toFixed(3)}%, ${fy.toFixed(3)}%) rotate(${fr.toFixed(3)}deg)`;

    // rVFC drives texture uploads when available; otherwise throttle to ~30fps
    // here so we don't hammer texImage2D at display refresh rate on Android.
    if (!USE_VIDEO_ALPHA && !this.useVFC && now - this.lastDrawAt >= 33) {
      this.drawCloudFrame();
      this.lastDrawAt = now;
    }

    this.rafId = requestAnimationFrame(this.tick);
  }

  destroy() {
    this.stop();
    if (this.observer) this.observer.disconnect();
    if (this.reveal) this.reveal.destroy();
    this.stopTracking?.();
  }
}
